import type { EventPricePoint } from "../../lib/types";
import { formatPrice } from "../../lib/utils";

function isBookablePricePoint(pp: EventPricePoint): boolean {
  return pp.availability !== "full";
}

interface PricePointSelectorProps {
  value: string;
  categoryPricePoints: EventPricePoint[];
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
}

/**
 * Category / price point selector. Shows all tiers in the current booking window;
 * fully booked tiers appear disabled.
 */
export function PricePointSelector({
  value,
  categoryPricePoints,
  onChange,
  disabled = false,
  required = false,
  error,
}: PricePointSelectorProps) {
  if (categoryPricePoints.length <= 1) {
    return null;
  }

  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${error ? "text-red-700" : "text-gray-700"}`}>
        Price Category
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border rounded-md px-3 py-2 text-sm ${
          error
            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
            : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
        } focus:outline-none focus:ring-2`}
        required={required}
        disabled={disabled}
      >
        <option value="">Select price category...</option>
        {categoryPricePoints.map((pp) => (
          <option
            key={pp.id}
            value={pp.id}
            disabled={!isBookablePricePoint(pp)}
          >
            {pp.label} -{" "}
            {pp.priceCents === 0
              ? "Free"
              : formatPrice(pp.priceCents, pp.currency)}
            {pp.depositCents &&
              ` (${formatPrice(pp.depositCents, pp.currency)} deposit)`}
            {pp.description && ` (${pp.description})`}
            {pp.availability === "full"
              ? " — Fully booked"
              : pp.availability === "nearly_full"
                ? " — limited places remaining"
                : ""}
          </option>
        ))}
      </select>
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
}
